/**
 * Every user-visible brand string comes from the repo-root brand.json (alias
 * "@brand"; see vite.brand.ts). Never write the product name in a component:
 * use `brand.name`. A full rename of internal names is scripts/rename_project.py.
 */
import raw from "@brand";
import { useEffect } from "react";

export interface Brand {
  name: string;
  slug: string;
  tagline: string;
  description: string;
  domain: string;
  supportEmail: string;
  legalName: string;
  /**
   * Full registered address: kept but shown nowhere for now (owner, 2026-10-24);
   * it comes back when proper GST tax invoices are built. Display `location`.
   */
  address?: string;
  /** Short location ("Bengaluru, Karnataka") shown everywhere: site, contact, policies, receipts. */
  location: string;
  city: string;
  /** State, for the Organization JSON-LD addressRegion. */
  region: string;
  /** Phone for investors (the Contact page; JSON-LD telephone). */
  phone: string;
  /** Contact person for the investors' phone (Contact page). */
  phoneContactName?: string;
  /** Contact person and phone for Research Analysts (Contact page). */
  raContactName?: string;
  raContactPhone?: string;
  /** The Pro Member agreement's telephone support line. */
  proSupportName?: string;
  proSupportPhone?: string;
  /** The WhatsApp Business number: the sales chatbot (site "Chat on WhatsApp" button). */
  whatsapp?: string;
  gstin: string;
  grievanceOfficer: string;
  /** Social profile URLs for the Organization JSON-LD (sameAs). */
  sameAs?: string[];
}

export const brand: Brand = raw;

/** "Find a Research Analyst · <Brand>", or the brand and tagline on the home page. */
export function pageTitle(title?: string | null): string {
  return title ? `${title} · ${brand.name}` : `${brand.name} · ${brand.tagline}`;
}

function setMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.content = content;
}

/** Set the tab title and the description / Open Graph tags for this page. */
export function useDocumentMeta(title?: string | null, description: string = brand.description) {
  useEffect(() => {
    const full = pageTitle(title);
    document.title = full;
    setMeta("name", "description", description);
    setMeta("property", "og:title", full);
    setMeta("property", "og:description", description);
    setMeta("name", "twitter:title", full);
    setMeta("name", "twitter:description", description);
  }, [title, description]);
}
